import React, { useEffect, useState } from "react"
import Breadcrumbs from "components/Common/Breadcrumb"
import Filters from "components/Common/Filters"
import Paginations from "components/Common/Paginations"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import TableShimmer from "components/Shared/TableShimmer"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import {
  Badge,
  Card,
  CardBody,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  Table,
  UncontrolledDropdown,
} from "reactstrap"
import {
  deleteQuestion,
  getAllRatingQuestions,
  updateQuestionStatus,
} from "store/actions/ratingsAction"
import { filters } from "components/Common/constants"
import Switch from "components/Shared/Switch"
import { showAlert } from "store/reducers/alertReducer"
import DeleteModal from "components/Common/DeleteModal"
import isPermissionEnabled from "common/isPermissionEnabled"
import { useTranslation } from "react-i18next"

const RatingQuestions = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t(`Rating Questions | Sign Burger`)

  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const {
    loading,
    itemsPerPage,
    totalCount,
    totalPages,
    currentPage,
    ratingQuestions,
    isLoading,
  } = useSelector(state => state.Ratings)

  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "")
  const [isApiCalled, setIsApiCalled] = useState(false)
  const [selectedFilters, setSelectedFilters] = useState({
    status: parseInt(searchParams.get("status") || "2"),
    sortOrder: parseInt(searchParams.get("sortOrder") || "1"),
  })
  const [selectedId, setSelectedId] = useState(null)
  const [itemIdToDelete, setItemIdToDelete] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const { userRole, accountType } = useSelector(state => state.Login)

  const [usersPermissions, setUsersPermissions] = useState({
    list: null,
    create: null,
    edit: null,
    delete: null,
  })

  // FIRST CHECK PERMISSION, IF DOES'T HAVE THEN REDIRECT TO DASHBOARD
  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      27,
      "rating_1",
      accountType
    )
    const isPermissionCreate = isPermissionEnabled(
      userRole,
      27,
      "rating_2",
      accountType
    )
    const isPermissionEdit = isPermissionEnabled(
      userRole,
      27,
      "rating_3",
      accountType
    )
    const isPermissionDelete = isPermissionEnabled(
      userRole,
      27,
      "rating_4",
      accountType
    )
    if (!isPermissionList) {
      navigate("/dashboard")
    }
    setUsersPermissions({
      list: isPermissionList,
      create: isPermissionCreate,
      edit: isPermissionEdit,
      delete: isPermissionDelete,
    })
  }, [userRole])

  useEffect(() => {
    fetchRatingQuestions()
  }, [])

  const fetchRatingQuestions = () => {
    const { status, sortOrder } = selectedFilters

    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      orderBy: sortOrder,
      keyword: searchTerm,
      status: status,
    }
    dispatch(getAllRatingQuestions(payload))
    setIsApiCalled(true)
  }

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
  }
  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: selectedValue,
    }))
  }

  const fetchRatingQuestionsApi = (page = 1) => {
    const { sortOrder, status } = selectedFilters

    const searchParams = new URLSearchParams()
    if (searchTerm) searchParams.set("search", searchTerm)
    searchParams.set("status", status.toString())
    searchParams.set("sortOrder", sortOrder.toString())
    searchParams.set("page", page.toString())

    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      status: Number(status), // 0 deactive, 1 active, 2 for all
      orderBy: Number(sortOrder),
      keyword: searchTerm || "",
    }

    dispatch(getAllRatingQuestions(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/rating-questions?${searchParams.toString()}`)
        }
      })
      .catch(error => {
        // Handle error if needed
      })
  }

  const handleSearch = () => {
    fetchRatingQuestionsApi(1)
  }

  const onPageChange = page => {
    fetchRatingQuestionsApi(page)
  }

  // useEffect(() => {
  //   if (isApiCalled) {
  //     if (searchTerm) {
  //       const delayDebounceFn = setTimeout(() => {
  //         handleSearch()
  //       }, 500)

  //       return () => clearTimeout(delayDebounceFn)
  //     } else {
  //       handleSearch()
  //     }
  //   }
  // }, [searchTerm, selectedFilters])

  const handleReset = () => {
    setSearchTerm("")
    const payload = {
      perPage: itemsPerPage,
      pageNo: 1,
      status: 2,
      orderBy: 1,
      keyword: "",
    }
    dispatch(getAllRatingQuestions(payload))
    navigate(`/rating-questions?page=${1}`)
    setSelectedFilters({
      status: 2,
      sortOrder: 1,
    })
  }

  const handleStatusUpdate = (id, status) => {
    setSelectedId(id)
    const payload = {
      id,
      status: status === 1 ? "0" : "1",
    }
    dispatch(updateQuestionStatus(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  const handleDelete = () => {
    const payload = {
      id: itemIdToDelete,
      status: "2", // 2 FOR DELETE
    }
    dispatch(deleteQuestion(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          setShowDeleteModal(false)
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <DataExportDropdown
                cardTitle={t("Rating Questions")}
                isAddBtn={loading ? false : usersPermissions.create}
                addBtnText={t("Add Question")}
                onAddBtnClick={() => navigate("/rating-questions/add")}
              />
              <CardBody>
                <Filters
                  filters={filters}
                  searchTerm={searchTerm}
                  handleSearchChange={handleSearchChange}
                  handleFilterChange={handleFilterChange}
                  handleSearch={handleSearch}
                  onReset={handleReset}
                  selectedFilters={selectedFilters}
                />
                <div>
                  {loading ? (
                    <TableShimmer row={7} col={5} />
                  ) : ratingQuestions.length === 0 ? (
                    <p className="text-center my-2">
                      {t("No rating questions found")}
                    </p>
                  ) : (
                    <Table className="table mb-0" hover>
                      <thead className="table-light">
                        <tr>
                          <th>{t("S. No.")}</th>
                          <th>{t("Question")}</th>
                          <th>{t("Type")}</th>
                          <th>{t("Scale")}</th>
                          <th>{t("Status")}</th>
                          <th>{t("Action")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ratingQuestions.map((item, index) => (
                          <tr
                            key={item._id}
                            onClick={() =>
                              navigate(
                                `/rating-questions/questions-details?id=${item._id}`
                              )
                            }
                            className="cursor-pointer"
                          >
                            <th scope="row">
                              {(currentPage - 1) * itemsPerPage + index + 1}
                            </th>
                            <td>
                              {language === "sa"
                                ? item?.question?.ar
                                : item?.question?.en}
                            </td>
                            <td>
                              {item.type === 0 && <p>{t("Star")}</p>}
                              {item.type === 1 && <p>{t("Scale")}</p>}
                            </td>
                            <td>{item.scale || "-"}</td>
                            <td>
                              {item.status === 1 ? (
                                <Badge className="bg-success">
                                  {t("Active")}
                                </Badge>
                              ) : (
                                <Badge className="bg-danger">
                                  {t("Deactive")}
                                </Badge>
                              )}
                            </td>
                            <td className="text-end">
                              <div className="d-flex align-items-center justify-content-end gap-2">
                                {usersPermissions.edit && (
                                  <Switch
                                    onClickHandler={e => {
                                      e.stopPropagation()
                                      handleStatusUpdate(item._id, item.status)
                                    }}
                                    isChecked={item.status === 1}
                                    disabled={
                                      item._id === selectedId && isLoading
                                    }
                                  />
                                )}
                                <UncontrolledDropdown>
                                  <DropdownToggle
                                    tag="a"
                                    className="card-drop"
                                    onClick={e => e.stopPropagation()}
                                  >
                                    <i className="mdi mdi-dots-vertical font-size-18"></i>
                                  </DropdownToggle>
                                  <DropdownMenu className="dropdown-menu-end">
                                    {usersPermissions.edit && (
                                      <DropdownItem
                                        onClick={e => {
                                          e.stopPropagation()
                                          navigate(
                                            `/rating-questions/edit?id=${item._id}`
                                          )
                                        }}
                                      >
                                        <i className="mdi mdi-pencil font-size-16 text-success me-1"></i>
                                        {t("Edit")}
                                      </DropdownItem>
                                    )}
                                    <DropdownItem
                                      onClick={e => {
                                        e.stopPropagation()
                                        navigate(
                                          `/rating-questions/questions-details?id=${item._id}`
                                        )
                                      }}
                                    >
                                      <i className="mdi mdi-eye font-size-16 text-primary me-1"></i>
                                      {t("View")}
                                    </DropdownItem>
                                    {usersPermissions.delete && (
                                      <DropdownItem
                                        onClick={e => {
                                          e.stopPropagation()
                                          setItemIdToDelete(item._id)
                                          setShowDeleteModal(true)
                                        }}
                                      >
                                        <i className="mdi mdi-trash-can font-size-16 text-danger me-1"></i>
                                        {t("Delete")}
                                      </DropdownItem>
                                    )}
                                  </DropdownMenu>
                                </UncontrolledDropdown>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </div>
                {!loading && ratingQuestions.length > 0 && (
                  <div className="mt-3">
                    <Paginations
                      data={ratingQuestions}
                      itemsPerPage={itemsPerPage}
                      onPageChange={onPageChange}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
      <DeleteModal
        show={showDeleteModal}
        isLoading={isLoading}
        onDeleteClick={handleDelete}
        onCloseClick={() => setShowDeleteModal(false)}
        buttonText={t("Delete")}
        bodyTitle={t("Are you sure you want to delete this {{name}}?", {
          name: t("Rating Question"),
        })}
      />
    </div>
  )
}

export default RatingQuestions
